import { apiFetch } from '../lib/apiFetch';

const API_BASE = import.meta.env.VITE_API_URL || '';

export type ArtifactValidationStatus = 'unknown' | 'pending' | 'passed' | 'failed' | 'skipped';

/**
 * An artifact as the backend registry stores it — the authoritative record,
 * with a real version number and a parent chain. Mirrors `ArtifactRecord` in
 * apps/api-gateway/src/services/project.ts (content omitted from list responses).
 */
export interface RegistryArtifact {
  id: string;
  projectId: string;
  name: string;
  /** 'generated-code' | 'patch' | 'requirement' | 'plan' | 'validation-report' | 'context-kernel' */
  type: string;
  sessionId?: string;
  version: number;
  parentArtifactId?: string;
  sourceRoute?: string;
  platformHint?: string;
  codeHash?: string;
  validationStatus: ArtifactValidationStatus;
  diffSummary?: string;
  metadata?: Record<string, unknown>;
  status: string;
  content?: string;
  contentType: string;
  createdAt: number;
  updatedAt: number;
}

export interface ListProjectArtifactsOptions {
  sessionId?: string;
  type?: string;
  limit?: number;
}

/**
 * Read the project's artifact registry. Artifacts are project-scoped, so a
 * session with no project has none — callers must fall back to the
 * message-derived view model in that case.
 */
export async function listProjectArtifacts(
  projectId: string,
  options: ListProjectArtifactsOptions = {},
): Promise<RegistryArtifact[]> {
  const params = new URLSearchParams();
  if (options.sessionId) params.set('sessionId', options.sessionId);
  if (options.type) params.set('type', options.type);
  if (options.limit != null) params.set('limit', String(options.limit));

  const query = params.toString();
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/artifacts${query ? `?${query}` : ''}`,
  );

  if (!response.ok) {
    throw new Error(`Failed to list project artifacts (${response.status})`);
  }

  const data = await response.json() as RegistryArtifact[] | null;
  return Array.isArray(data) ? data : [];
}

/** Fetch a single artifact including its `content`. */
export async function getProjectArtifact(
  projectId: string,
  artifactId: string,
): Promise<RegistryArtifact | null> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(artifactId)}`,
  );

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to load artifact (${response.status})`);
  }

  return await response.json() as RegistryArtifact;
}

export function buildArtifactDownloadUrl(projectId: string, artifactId: string): string {
  return `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(artifactId)}/download`;
}
